import {
  FEDERAL_WASTE_CODE_FORMAT_REASON,
  OUTS_WASTE_CODE_REASON,
  PHRM_WASTE_CODE_REASON,
  UNRECOGNIZED_STATE_WASTE_CODE_REASON,
  UNRECOGNIZED_WASTE_CODE_REASON,
} from "encamp-compliance-engine/src/schemas/rcra/wasteCodeMessages";
import {
  isValidStateWasteCode,
  isValidStateWasteCodeInAnyState,
} from "encamp-shared/src/waste/stateWasteCodes";
import {
  isOutsCode,
  isPhrmCode,
  isValidStandardFederalWasteCode,
} from "encamp-shared/src/waste/validCodes";

/**
 * Client-side waste-code validity checks for the Filing Notifications
 * sub-record dialogs (§13 episodic waste lines, §16 HSM activities). The
 * dialogs hold local draft forms, so the step's server-side validator
 * (`submissionRcraFilingNotificationsInputValidator`) can't reach them until
 * the dialog commits — these pure functions give the chips live red
 * highlighting instead. They call the same encamp-shared predicates and use
 * the same reason strings as the compliance-engine rules, so a code flagged
 * here is flagged there too, with the same wording.
 */
export type InvalidWasteCode = { code: string; reason: string };

export function getInvalidFederalWasteCodes(codes: string[]): InvalidWasteCode[] {
  return codes.flatMap((code) => {
    // Filing-notification lines carry no source/form codes, so PHRM codes
    // (valid only with source G61 + form W005) always get the guidance —
    // same posture as the compliance-engine rules.
    if (isPhrmCode(code)) {
      return [{ code, reason: PHRM_WASTE_CODE_REASON }];
    }
    if (!isValidStandardFederalWasteCode(code)) {
      return [{ code, reason: FEDERAL_WASTE_CODE_FORMAT_REASON }];
    }
    return [];
  });
}

/**
 * A filing may legitimately carry valid state codes from states other than the
 * facility's own, so any state's recognized code passes; the facility state
 * additionally unlocks that state's wildcards (e.g. TX facility-specific
 * codes). OUTS markers are never filable. Without a facility state the
 * engine's §13 rule skips state codes entirely, so nothing is flagged here
 * either — a red chip must always have a matching persisted issue.
 */
export function getInvalidStateWasteCodes(
  codes: string[],
  facilityState: string | null
): InvalidWasteCode[] {
  if (!facilityState) {
    return [];
  }
  return codes.flatMap((code) => {
    if (isOutsCode(code)) {
      return [{ code, reason: OUTS_WASTE_CODE_REASON }];
    }
    if (isStateValid(code, facilityState)) {
      return [];
    }
    return [{ code, reason: UNRECOGNIZED_STATE_WASTE_CODE_REASON }];
  });
}

/** §16 waste codes are a mixed list: a code passes as federal OR state. */
export function getInvalidHsmWasteCodes(
  codes: string[],
  facilityState: string | null
): InvalidWasteCode[] {
  return codes.flatMap((code) => {
    if (isOutsCode(code)) {
      return [{ code, reason: OUTS_WASTE_CODE_REASON }];
    }
    if (isStateValid(code, facilityState)) {
      return [];
    }
    // HSM activities carry no source/form codes, so PHRM codes (valid only
    // with source G61 + form W005) get the targeted guidance rather than the
    // generic mixed-list reason — same posture as the compliance-engine rule.
    if (isPhrmCode(code)) {
      return [{ code, reason: PHRM_WASTE_CODE_REASON }];
    }
    if (isValidStandardFederalWasteCode(code)) {
      return [];
    }
    return [{ code, reason: UNRECOGNIZED_WASTE_CODE_REASON }];
  });
}

function isStateValid(code: string, facilityState: string | null): boolean {
  if (facilityState && isValidStateWasteCode(code, facilityState)) {
    return true;
  }
  return isValidStateWasteCodeInAnyState(code);
}
